import { useCallback, useSyncExternalStore } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { usePref } from '@/hooks/use-pref';
import { useIsAdmin } from '@/hooks/usePermission';
import { UI_PREFS } from '@/lib/ui-prefs';
import { useAuthStore } from '@/stores/authStore';
import { useTaskStore } from '@/stores/taskStore';
import { mentionsTaskRef } from './chat-view';

/**
 * The chat panel beside other pages (T-78): whether it's open (per browser) and the chat it shows (per
 * person; none is New chat). Both are kept, so it follows you round the app and through a reload.
 * It shares the right edge with the Tasks panel: opening one closes the other.
 */
export function useChatPanel() {
  const me = useAuthStore((s) => s.user?.id);
  const [open, setOpenPref] = usePref(UI_PREFS.chatPanelOpen);
  const [sessionId, setSessionId] = usePref(UI_PREFS.chatPanelSession, { scopeId: me });
  const setOpen = useCallback(
    (next: boolean) => {
      if (next) useTaskStore.getState().setPanel({ open: false, taskId: null });
      setOpenPref(next);
    },
    [setOpenPref]
  );
  return { open, setOpen, sessionId, setSessionId };
}

/** The Chat page has the whole of Chat, so the panel and its button stay out of the way there. */
export function chatPanelHiddenOn(pathname: string): boolean {
  return pathname === '/chat' || pathname.startsWith('/chat/');
}

/** Whether the panel shows on this page: for admins, while it's open, on every page but Chat's own. */
export function useChatPanelShown(pathname: string): boolean {
  const isAdmin = useIsAdmin();
  const [open] = usePref(UI_PREFS.chatPanelOpen);
  return isAdmin && open && !chatPanelHiddenOn(pathname);
}

/**
 * How the panel sits: `dock` beside the page, which narrows for it (from 1024px, where the page keeps
 * at least 448px beside the sidebar and a 320px panel); `sheet` over the right of the page, below that;
 * `cover` over the whole screen on a phone (below 640px), where the page behind can't be reached.
 */
export type ChatPanelLayout = 'dock' | 'sheet' | 'cover';

const SHEET_QUERY = '(max-width: 1023px)';
const COVER_QUERY = '(max-width: 639px)';

function subscribeLayout(onChange: () => void) {
  const queries = [window.matchMedia(SHEET_QUERY), window.matchMedia(COVER_QUERY)];
  queries.forEach((q) => q.addEventListener('change', onChange));
  return () => queries.forEach((q) => q.removeEventListener('change', onChange));
}

function readLayout(): ChatPanelLayout {
  if (window.matchMedia(COVER_QUERY).matches) return 'cover';
  return window.matchMedia(SHEET_QUERY).matches ? 'sheet' : 'dock';
}

export function useChatPanelLayout(): ChatPanelLayout {
  return useSyncExternalStore(subscribeLayout, readLayout, () => 'dock');
}

/** The task open on the page (over the Board or the Inbox, or a project's Board), for New chat to mention. */
const TASK_ROUTES = ['/board/:taskId', '/inbox/:taskId', '/p/:slug/board/:taskId'];

export function openTaskIdOn(pathname: string): string | null {
  for (const pattern of TASK_ROUTES) {
    const id = matchPath(pattern, pathname)?.params.taskId;
    if (id) return id;
  }
  return null;
}

/** The key a chat's unsent message is kept under: its session id, or `new` for New chat. */
export const NEW_CHAT_DRAFT = 'new';

/**
 * Whether the chat panel is over this page (below 1024px), so a pop-up under it steps aside while it's open
 * (TaskPopup, SF-T-141) and comes back when it closes.
 */
export function useChatOverPage(): boolean {
  const { pathname } = useLocation();
  const shown = useChatPanelShown(pathname);
  const layout = useChatPanelLayout();
  return shown && layout !== 'dock';
}

/**
 * Whether the chat panel can come over this page (below 1024px, for admins, on any page but Chat's own),
 * open or not. A pop-up that must step aside for it stays non-modal throughout: switching a Radix dialog
 * between modal and not remounts its contents, which would lose a half-written comment (TaskPopup).
 */
export function useChatCanCoverPage(): boolean {
  const { pathname } = useLocation();
  const isAdmin = useIsAdmin();
  const layout = useChatPanelLayout();
  return isAdmin && layout !== 'dock' && !chatPanelHiddenOn(pathname);
}

/** The task "Chat about this task" was last used on (SF-T-141), for New chat when the page's URL doesn't name it. */
export interface ChatAboutTask {
  id: string;
  ref: string;
  title: string;
  projectId: string | null;
}

let aboutTask: ChatAboutTask | null = null;
const aboutTaskListeners = new Set<() => void>();

function setChatAboutTask(task: ChatAboutTask | null) {
  aboutTask = task;
  aboutTaskListeners.forEach((listener) => listener());
}

/** Forgets it once a chat about it has started. */
export function clearChatAboutTask() {
  if (aboutTask) setChatAboutTask(null);
}

const subscribeAboutTask = (onChange: () => void) => {
  aboutTaskListeners.add(onChange);
  return () => void aboutTaskListeners.delete(onChange);
};

/** The task "Chat about this task" was last used on, while New chat hasn't started a chat about it. */
export function useRememberedChatTask(): ChatAboutTask | null {
  return useSyncExternalStore(subscribeAboutTask, () => aboutTask, () => null);
}

/**
 * SF-T-141: Chat about a task (the task's ⋯ menu, admins): the panel opens on New chat with the task in the
 * message (its project is the page's, so it's the focus project), on a phone too, where the task steps
 * aside until the chat closes.
 */
export function useChatAboutTask() {
  const me = useAuthStore((s) => s.user?.id);
  const { setOpen, setSessionId } = useChatPanel();
  const [draft, setDraft] = usePref(UI_PREFS.chatDraft, { chat: NEW_CHAT_DRAFT, scopeId: me });
  return useCallback(
    (task: ChatAboutTask) => {
      // Remembered for New chat: the task may not be in the page's URL (the Tasks panel closes as the chat opens).
      setChatAboutTask(task);
      if (!mentionsTaskRef(draft, task.ref)) setDraft(`${task.ref} (${task.title}) ${draft}`.trimEnd() + ' ');
      setSessionId(null);
      setOpen(true);
    },
    [draft, setDraft, setSessionId, setOpen]
  );
}

/** Whether the chat panel is docked beside this page, so a pop-up over the page leaves it usable (TaskPopup). */
export function useChatDockedBeside(): boolean {
  const { pathname } = useLocation();
  const shown = useChatPanelShown(pathname);
  const layout = useChatPanelLayout();
  return shown && layout === 'dock';
}

/**
 * Whether an event happened in the chat panel (or a menu opened from it), for a pop-up that stays open
 * then. A pop-up hears of a click only once it's over, so an element the click removed (the panel's
 * Mention button) counts too: it's no longer in the page to tell where it was.
 */
export function isInChatPanel(target: EventTarget | null): boolean {
  return target instanceof Element && (!target.isConnected || !!target.closest('[data-chat-panel], [data-radix-popper-content-wrapper]'));
}
